import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../utils/cn";
import Icon from "../AppIcon";
import { getAdminUser } from "../../utils/adminAuth";
import useScrollLock from "../../hooks/useScrollLock";
import Button from "../ui/Button";
import ConfirmationModal from "../ui/ConfirmationModal";

const AdminSidebar = ({ sidebarOpen, mobileMenuOpen, setMobileMenuOpen, menuItems }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getAdminUser();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  useScrollLock(mobileMenuOpen && window.innerWidth < 1024);

  const isActiveRoute = (path) => {
    const matches = location.pathname === path || location.pathname.startsWith(path + "/");
    if (!matches) return false;
    const hasMoreSpecific = menuItems.some(
      (item) =>
        item.path !== path &&
        item.path.length > path.length &&
        (location.pathname === item.path || location.pathname.startsWith(item.path + "/"))
    );
    return !hasMoreSpecific;
  };

  return (
    <motion.aside
      initial={false}
      animate={{
        width: sidebarOpen ? 280 : 80,
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={cn(
        "fixed top-0 left-0 h-full z-50 flex flex-col overflow-hidden",
        "bg-card border-r border-border shadow-lg",
        "transition-transform duration-300 ease-in-out",
        mobileMenuOpen ? "translate-x-0" : "lg:translate-x-0 -translate-x-full"
      )}
    >
      {/* Logo */}
      <div className="p-4 sm:p-5 border-b border-border shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center shrink-0">
              <img src="/assets/images/Endo-Logo-small.png" alt="Endosurgery Logo" className="w-full h-full object-contain" />
            </div>
            <AnimatePresence>
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.2 }}
                  className="ml-3 min-w-0"
                >
                  <h1 className="text-base sm:text-lg font-bold text-text-primary truncate">
                    Endosurgery Admin
                  </h1>
                  <p className="text-xs text-text-secondary truncate">Management Panel</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary rounded-lg"
            aria-label="Close menu"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 sm:p-4 overflow-y-auto">
        <ul className="space-y-1.5 sm:space-y-2">
          {menuItems.map((item) => {
            const active = isActiveRoute(item.path);
            return (
              <li key={item.path}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(item.path);
                    if (window.innerWidth < 1024) setMobileMenuOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center px-3 py-3 sm:py-3.5 rounded-lg transition-all duration-200 text-base sm:text-[15px]",
                    active
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "text-text-secondary hover:bg-muted hover:text-text-primary"
                  )}
                >
                  <Icon
                    name={item.icon}
                    size={22}
                    className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0"
                    color={active ? "#FFFFFF" : undefined}
                    strokeWidth={2}
                  />
                  <AnimatePresence>
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="ml-3 sm:ml-3.5 font-semibold truncate text-left"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User & Logout */}
      <div className="p-3 sm:p-4 border-t border-border shrink-0">
        <div className="flex items-center mb-3 sm:mb-4">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
            <Icon name="User" size={14} className="text-text-secondary sm:w-4 sm:h-4" />
          </div>
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="ml-2 sm:ml-3 flex-1 min-w-0"
              >
                <p className="text-xs sm:text-sm font-medium text-text-primary truncate">
                  {user?.full_name || user?.email || "Admin"}
                </p>
                <p className="text-xs text-text-secondary truncate capitalize">
                  {user?.role ? user.role.replace("_", " ") : "Admin"}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Button
          onClick={() => setLogoutModalOpen(true)}
          variant="outline"
          size="sm"
          className="w-full text-xs sm:text-sm bg-card text-text-primary border-border hover:bg-primary hover:text-primary-foreground hover:border-primary"
        >
          <Icon name="LogOut" size={14} className="sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          {sidebarOpen && <span className="truncate">Logout</span>}
        </Button>
      </div>

      {logoutModalOpen &&
        createPortal(
          <div className="admin-layout" style={{ position: "fixed", inset: 0, zIndex: 10050 }}>
            <ConfirmationModal
              isOpen={logoutModalOpen}
              title="Logout"
              message="Are you sure you want to logout from the admin panel?"
              onConfirm={async () => {
                setLogoutModalOpen(false);
                const { adminLogout } = await import("../../api/adminClient");
                await adminLogout(false);
                navigate("/admin/login", { replace: true });
              }}
              onCancel={() => setLogoutModalOpen(false)}
              confirmText="Logout"
              cancelText="Cancel"
              confirmVariant="primary"
            />
          </div>,
          document.body
        )}
    </motion.aside>
  );
};

export default AdminSidebar;
